<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { useRoute } from "vue-router";
import LayoutMain from "@/components/v2/Layouts/LayoutMain.vue";
import { TasksService } from "@/services/v2/tasks";
import { useTasksStore } from "@/stores/v2";
import { normalizeRouteParam } from "@/utils/v2/routes";
import { onMounted, ref, nextTick, computed } from "vue";
import { useTask } from "@/composables/tasks/useTask";
import { usePreChallenge } from "@/composables/tasks/pre-challenge/usePreChallenge";
import { useNotifications } from "@/composables/useNotifications";

import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import InstructionsModal from "@/components/v2/Tasks/AnimalTransfer/Modals/InstructionsModal.vue";
import TaskDependencyWarningModal from "@/components/v2/Tasks/Task/TaskDependencyWarningModal.vue";

const { startObserver } = useTask();

const route = useRoute();
const taskKey = normalizeRouteParam(route.params.key)

const showCameraModal = ref(false);
const currentDilutionKey = ref<string>('');
const videoStream = ref<MediaStream | null>(null);
const videoElement = ref<HTMLVideoElement | null>(null);
const canvasElement = ref<HTMLCanvasElement | null>(null);

const showImagePreview = ref(false);
const previewImageUrl = ref('');
const activeDropdown = ref<string | null>(null);
const instructionsModal = ref(false);
const isLoading = ref(true);
const isFinishing = ref(false);
const error = ref('');

// ===================================================================
// Composables
// ===================================================================
const { activeTask } = useTask();
const { showTaskCompletionSuccess, showError } = useNotifications();
const {
    dependentChallengeTask,
    completedDilutions,
    incompleteDilutions,
    completionProgress,
    isAllCompleted,
    markDilutionComplete,
    markDilutionIncomplete,
    addPhoto,
    removePhoto,
    getSourceDilutionName,
} = usePreChallenge();

const displayDilutions = computed(() => [
    ...incompleteDilutions.value,
    ...completedDilutions.value,
]);

const init = async () => {
    isLoading.value = true;
    error.value = '';

    if (!taskKey) {
        error.value = 'Invalid task key';
        isLoading.value = false;
        return;
    }

    try {
        const task = await TasksService.startTask(taskKey);
        if (!task) {
            error.value = 'Task not found';
            isLoading.value = false;
            return;
        }
        await useTasksStore().setActiveTask(task);
        startObserver();
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load task';
    } finally {
        isLoading.value = false;
    }
};

const handleRetry = () => {
    init();
};

// ===================================================================
// Methods
// ===================================================================
const formatStatus = (status: string) => {
    return status
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase());
};

const readFileAsBase64 = (file: File): Promise<{ name: string, data: string }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve({
                name: file.name,
                data: reader.result as string
            });
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
};

const toggleDropdown = (dilutionKey: string) => {
    if (activeDropdown.value === dilutionKey) {
        activeDropdown.value = null;
    } else {
        activeDropdown.value = dilutionKey;
    }
};

const closeDropdown = () => {
    activeDropdown.value = null;
};

const openImagePreview = (imageUrl: string) => {
    previewImageUrl.value = imageUrl;
    showImagePreview.value = true;
};

const closeImagePreview = () => {
    showImagePreview.value = false;
    previewImageUrl.value = '';
};

const formatScientificNotation = (value: number | string | null) => {
    // Handle null, undefined, or empty values
    if (value === null || value === undefined || value === '') {
        return 'Not calculated';
    }

    const num = typeof value === 'string' ? parseFloat(value) : value;
    
    // Handle NaN or invalid numbers
    if (isNaN(num) || !isFinite(num)) {
        return 'Invalid value';
    }

    const exp = num.toExponential(2);
    const [coefficient, exponent] = exp.split('e');
    const exp_num = parseInt(exponent);

    const superscripts: Record<string, string> = {
        '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
        '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
        '-': '⁻', '+': '⁺'
    };

    const superscriptExp = exp_num.toString().split('').map(char => superscripts[char] || char).join('');

    return `${coefficient} × 10${superscriptExp}`;
};

const handlePhotoUpload = async (dilutionKey: string, event: Event) => {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;

    try {
        const photoDataArray = await Promise.all(
            Array.from(files).map(file => readFileAsBase64(file))
        );

        console.log('Photo data array:', photoDataArray);
        // Use the addPhoto method from composable to add photos directly to activeTask
        photoDataArray.forEach(photo => {
            addPhoto(dilutionKey, photo.data);
        });
        console.log('Photos added to dilution:', dilutionKey);
    } catch (error) {
        console.error('Error reading files:', error);
        alert('Failed to read and encode the selected files.');
    }
};

const startCamera = async (dilutionKey: string) => {
    currentDilutionKey.value = dilutionKey;
    showCameraModal.value = true;

    await nextTick();

    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: 'environment', // Use back camera
                width: { ideal: 1280 },
                height: { ideal: 720 }
            }
        });

        videoStream.value = stream;

        if (videoElement.value) {
            videoElement.value.srcObject = stream;
            videoElement.value.play();
        }
    } catch (error) {
        console.error('Error accessing camera:', error);
        alert('Unable to access camera. Please check permissions.');
        closeCameraModal();
    }
};

const capturePhoto = async () => {
    if (!videoElement.value || !canvasElement.value || !videoStream.value) return;

    const video = videoElement.value;
    const canvas = canvasElement.value;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(async (blob) => {
        if (blob && currentDilutionKey.value) {
            try {
                // Create a File from the blob
                const file = new File([blob], `dilution-${currentDilutionKey.value}-${Date.now()}.jpg`, {
                    type: 'image/jpeg'
                });

                const photoData = await readFileAsBase64(file);
                console.log('Camera photo data:', photoData);
                // Use the addPhoto method from composable to add photo directly to activeTask
                addPhoto(currentDilutionKey.value, photoData.data);
                console.log('Camera photo added to dilution:', currentDilutionKey.value);
                closeCameraModal();
            } catch (error) {
                console.error('Error processing captured photo:', error);
                alert('Failed to save photo');
            }
        }
    }, 'image/jpeg', 0.8);
};

const closeCameraModal = () => {
    if (videoStream.value) {
        videoStream.value.getTracks().forEach(track => track.stop());
        videoStream.value = null;
    }
    showCameraModal.value = false;
    currentDilutionKey.value = '';
};

const handleCameraPhoto = (dilutionKey: string) => {
    if (navigator.mediaDevices) {
        startCamera(dilutionKey);
    } else {
        alert('Camera not supported on this device');
        handleFilePhoto(dilutionKey);
    }
};

const handleFilePhoto = (dilutionKey: string) => {
    const input = document.getElementById(`file-${dilutionKey}`) as HTMLInputElement;
    input?.click();
};

const showInstructions = () => {
    instructionsModal.value = true;
};

const toggleInstructionsModal = () => {
    instructionsModal.value = !instructionsModal.value;
};

const handleFinishTask = async () => {
    if (!activeTask.value) return;

    isFinishing.value = true;

    try {
        console.log('Starting handleFinishTask');
        console.log('activeTask.value:', activeTask.value);
        console.log('Task details:', activeTask.value.details);
        
        // No need to sync - we're working directly with activeTask data
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing task:', error);
        showError("Task Completion Failed", "Failed to complete task. Please try again.");
    } finally {
        isFinishing.value = false;
    }
};

onMounted(() => {
    init();
    
    // Close dropdown when clicking outside
    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        if (!target.closest('.relative')) {
            activeDropdown.value = null;
        }
    });
});
</script>

<template>
    <LayoutMain>
        <template #mainArea>
            <TaskDependencyWarningModal />

            <!-- Loading State -->
            <div v-if="isLoading" class="flex justify-center items-center h-full">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="flex flex-col justify-center items-center h-full">
                <div class="text-red-500 mb-4">{{ error }}</div>
                <button @click="handleRetry" class="px-4 py-2 bg-primary text-white rounded">Retry</button>
            </div>

            <!-- Main Content -->
            <div v-else-if="activeTask" class="p-4 sm:p-6 lg:p-8">
                <!-- Header -->
                <div class="mb-6">
                    <h1 class="text-2xl font-bold text-gray-800">{{ activeTask.title }}</h1>
                    <p class="text-sm text-gray-500">{{ activeTask.description }}</p>
                </div>

                <!-- Dependent Challenge Task Notification -->
                <div v-if="dependentChallengeTask" class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="font-semibold text-blue-900">Dependent Challenge Task</h3>
                            <p class="text-blue-800">{{ dependentChallengeTask.title }}</p>
                        </div>
                        <span class="px-3 py-1 text-sm font-medium rounded-full" :class="{
                            'bg-green-100 text-green-800': dependentChallengeTask.status === 'finished',
                            'bg-yellow-100 text-yellow-800': dependentChallengeTask.status === 'pending',
                            'bg-blue-100 text-blue-800': dependentChallengeTask.status === 'in_progress'
                        }">{{ formatStatus(dependentChallengeTask.status) }}</span>
                    </div>
                </div>

                <!-- Main Content -->
                <div class="space-y-6">
                    <div class="flex justify-between items-center">
                        <h2 class="text-xl font-semibold text-gray-700">Dilutions</h2>
                        <span class="text-sm font-medium text-gray-600">Progress: {{ completionProgress.toFixed(0) }}%</span>
                    </div>
                    
                    <!-- Dilutions Table -->
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div v-if="displayDilutions.length === 0" class="text-center py-8 text-gray-500">
                            No dilutions available
                        </div>

                        <div v-else class="overflow-x-auto">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Dilution
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Source
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Volume
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Resulting Concentration
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Instructions
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Photos
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-200">
                                    <tr
                                        v-for="dilution in displayDilutions"
                                        :key="dilution.key"
                                        :class="dilution.completed ? 'bg-green-50' : 'bg-white hover:bg-gray-50'"
                                    >
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div
                                                class="font-medium"
                                                :class="dilution.completed ? 'text-green-800' : 'text-primary'"
                                            >
                                                Dilution #{{ dilution.dilutionNumber }}
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                {{ dilution.dilutionRatio }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                                            <span
                                                class="px-2 py-0.5 text-xs font-medium rounded-full"
                                                :class="dilution.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                                            >
                                                {{ dilution.completed ? 'Completed' : 'In progress' }}
                                            </span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {{ getSourceDilutionName(dilution) }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div class="font-medium">
                                                <span v-if="dilution.totalVolume !== null && dilution.totalVolume !== undefined">
                                                    {{ dilution.totalVolume }}
                                                </span>
                                                <span v-else-if="dilution.volumeAdded !== null && dilution.volumeAdded !== undefined && dilution.diluentVolume !== null && dilution.diluentVolume !== undefined">
                                                    {{ dilution.volumeAdded + dilution.diluentVolume }}
                                                </span>
                                                <span v-else class="text-gray-400">
                                                    N/A
                                                </span>
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                Added: {{ dilution.volumeAdded ?? 'N/A' }} · {{ dilution.diluentType || 'Diluent' }}: {{ dilution.diluentVolume ?? 'N/A' }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {{ formatScientificNotation(dilution.resultingConcentration) }}
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-700">
                                            <div class="max-w-md whitespace-pre-wrap">
                                                {{ dilution.instructions }}
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 text-sm">
                                            <div v-if="dilution.photos && dilution.photos.length > 0" class="grid grid-cols-3 gap-2 min-w-[9rem]">
                                                <div v-for="(photo, index) in dilution.photos" :key="index" class="relative group">
                                                    <img
                                                        :src="photo"
                                                        alt="Photo"
                                                        class="w-20 h-14 object-cover rounded-md cursor-pointer"
                                                        @click="openImagePreview(photo)"
                                                    >
                                                    <button
                                                        @click="removePhoto(dilution.key, index)"
                                                        class="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            </div>
                                            <span v-else class="text-gray-400 text-sm">No photos</span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                                            <div class="flex items-center gap-2">
                                                <template v-if="!dilution.completed">
                                                    <button
                                                        @click="markDilutionComplete(dilution.key)"
                                                        class="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                                                    >
                                                        Complete
                                                    </button>
                                                    <div class="relative">
                                                        <button
                                                            @click="toggleDropdown(dilution.key)"
                                                            class="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                                                        >
                                                            Add Photo ▼
                                                        </button>
                                                        <div
                                                            v-show="activeDropdown === dilution.key"
                                                            class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 min-w-[120px]"
                                                        >
                                                            <button @click="handleCameraPhoto(dilution.key); closeDropdown()" class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">Camera</button>
                                                            <button @click="handleFilePhoto(dilution.key); closeDropdown()" class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">Upload File</button>
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="file"
                                                        :id="`file-${dilution.key}`"
                                                        class="hidden"
                                                        accept="image/*"
                                                        multiple
                                                        @change="handlePhotoUpload(dilution.key, $event)"
                                                    >
                                                </template>
                                                <template v-else>
                                                    <button
                                                        @click="markDilutionIncomplete(dilution.key)"
                                                        class="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                                    >
                                                        Undo
                                                    </button>
                                                </template>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            
            <div v-else class="text-center py-10">
                <p>Loading task...</p>
            </div>

            <!-- Footer -->
            <FooterActions>
                <template #start>
                    <button
                        @click="showInstructions"
                        class="w-full"
                    >
                        Instructions
                    </button>
                </template>
                <template #end>
                    <button
                        @click="handleFinishTask"
                        class="w-full flex items-center justify-center"
                        :disabled="!isAllCompleted || isFinishing"
                        :class="{ 'opacity-50 cursor-not-allowed': !isAllCompleted || isFinishing }"
                    >
                        <div v-if="isFinishing" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {{ isFinishing ? 'Finishing...' : 'Finish Task' }}
                    </button>
                </template>
            </FooterActions>
            
            <!-- Camera Modal -->
            <div
                v-if="showCameraModal"
                class="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center"
            >
                <div class="bg-white p-4 rounded-lg w-full max-w-2xl">
                    <video ref="videoElement" class="w-full rounded" autoplay playsinline></video>
                    <canvas ref="canvasElement" class="hidden"></canvas>
                    <div class="mt-4 flex justify-center space-x-4">
                        <button @click="capturePhoto" class="px-4 py-2 bg-primary text-white rounded">Capture</button>
                        <button @click="closeCameraModal" class="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                    </div>
                </div>
            </div>

            <!-- Image Preview Modal -->
            <div
                v-if="showImagePreview"
                class="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center"
                @click="closeImagePreview"
            >
                <img :src="previewImageUrl" alt="Image Preview" class="max-w-4xl max-h-[90vh] object-contain rounded-lg">
                <button
                    @click.stop="closeImagePreview"
                    class="absolute top-4 right-4 text-white text-3xl font-bold"
                >&times;</button>
            </div>

            <!-- Instructions Modal -->
            <InstructionsModal :showModal="instructionsModal" @close="toggleInstructionsModal" />
        </template>
    </LayoutMain>
</template>
